import { Box } from '@mui/material';
import { BreakpointProperties } from '../../../../hooks/useBreakpoint';
import { useScreenOrientation } from '../../../../hooks/useScreenOrientation';
import {
  CfDataGridCardViewDefinition,
  CfDataGridDisplayMode,
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfRecomputeRowHeightsRefCallback,
  NavigableElement,
} from '../../CfDataGridProps';
import { getPropertyByBreakpoint } from '../../utility/breakpoint';
import { KeyboardNavigator } from '../../utility/keyboard-navigation';
import { CfDataGridCardViewBody } from './body/CfDataGridCardViewBody';
import { CfDataGridCardViewVirtualizedBody } from './body/CfDataGridCardViewVirtualizedBody';

export function CfDataGridCardView<TItem, TAppState>(props: {
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>;
  keyboardNavigator: KeyboardNavigator;
  breakpointProperties: BreakpointProperties;
  onNextPageRequested: (pageIndex: number) => void;
  recomputeRowHeightsRef: CfRecomputeRowHeightsRefCallback;
}): JSX.Element {
  if (!props.dataGridProps.cardView) return <></>;

  const breakpoint = props.breakpointProperties.breakpoint;
  const cardViewDefinition = getPropertyByBreakpoint<CfDataGridCardViewDefinition<TItem>>(
    props.dataGridProps.cardView,
    breakpoint
  );

  const orientation = useScreenOrientation();

  props.keyboardNavigator.reset();
  const setNavigableElement = (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) =>
    props.keyboardNavigator.registerNavigableElement(navigableElement, { rowIndex, columnIndex });
  const focusClosestEnabledElement = (rowIndex: number, elementIndex?: number) =>
    props.keyboardNavigator.focusClosestEnabledElement(rowIndex, elementIndex);

  return (
    <Box {...cardViewDefinition.gridProps}>
      {cardViewDefinition.virtualizedProps ? (
        <CfDataGridCardViewVirtualizedBody
          dataGridProps={props.dataGridProps}
          cardViewDefinition={cardViewDefinition}
          setNavigableElement={setNavigableElement}
          breakpointProperties={props.breakpointProperties}
          onNextPageRequested={props.onNextPageRequested}
          orientation={orientation}
          focusClosestEnabledElement={focusClosestEnabledElement}
          recomputeRowHeightsRef={props.recomputeRowHeightsRef}
        />
      ) : (
        <CfDataGridCardViewBody
          dataGridProps={props.dataGridProps}
          cardViewDefinition={cardViewDefinition}
          setNavigableElement={setNavigableElement}
          orientation={orientation}
        />
      )}
    </Box>
  );
}
